import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import type { PaymentChannelOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent, CardHeader } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const PROVIDER_KEYS: Record<string, string> = {
  paypal: "payments.providerPaypal",
  alipay: "payments.providerAlipay",
  wechat: "payments.providerWechat",
  manual: "payments.providerManual",
  mock: "payments.providerMock",
};

/** 编辑态的一行密钥：key + 新值（空 = 不动 / 清空后提交 = 删除）。 */
interface SecretRow {
  key: string;
  value: string;
  /** 已存在的 key：掩码展示用 */
  mask?: string;
}

export function ChannelsPage() {
  const { t } = useTranslation();
  const channels = useQuery({
    queryKey: ["admin", "settings", "channels"],
    queryFn: () => adminSettingsApi.listPaymentChannels(),
  });

  if (channels.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (channels.error) {
    return <ErrorState error={channels.error} onRetry={() => channels.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader title={t("settings.channelsTitle")} subtitle={t("settings.channelsSubtitle")} />
      <div className="grid gap-4 lg:grid-cols-2">
        {channels.data?.map((channel) => (
          <ChannelCard key={channel.provider} channel={channel} />
        ))}
      </div>
    </div>
  );
}

function ChannelCard({ channel }: { channel: PaymentChannelOut }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState(channel.mode);
  const [enabled, setEnabled] = useState(channel.enabled);
  const [remark, setRemark] = useState(channel.remark ?? "");
  const [secrets, setSecrets] = useState<SecretRow[]>([]);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    setMode(channel.mode);
    setEnabled(channel.enabled);
    setRemark(channel.remark ?? "");
    setSecrets(
      Object.entries(channel.secret_mask).map(([key, mask]) => ({ key, value: "", mask })),
    );
  }, [channel]);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, string> = {};
      for (const row of secrets) {
        if (row.value !== "") patch[row.key] = row.value;
      }
      // 被删除的已有 key -> 提交空串删除
      for (const key of Object.keys(channel.secret_mask)) {
        if (!secrets.some((row) => row.key === key)) patch[key] = "";
      }
      return adminSettingsApi.patchPaymentChannel(channel.provider, {
        mode: mode as "mock" | "live",
        enabled,
        remark: remark || null,
        secrets: Object.keys(patch).length > 0 ? patch : null,
      });
    },
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("common.saved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings", "channels"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) => setFeedback({ kind: "err", text: translateError(err, t) }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-5 pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">
            {t(PROVIDER_KEYS[channel.provider] ?? channel.provider)}
          </h2>
          {channel.has_secrets ? (
            <Badge variant="success">{t("common.enabled")}</Badge>
          ) : (
            <Badge variant="secondary">{t("common.notConfigured")}</Badge>
          )}
        </div>
        {channel.updated_at ? (
          <p className="text-xs text-muted">{formatDateTime(channel.updated_at)}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("settings.channelMode")}</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mock">{t("settings.modeMock")}</SelectItem>
                <SelectItem value="live">{t("settings.modeLive")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={enabled} onCheckedChange={setEnabled} />
              {t("settings.channelEnabled")}
            </label>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("common.remark")}</Label>
          <Input
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={t("settings.channelRemarkPlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t("settings.channelSecrets")}</Label>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSecrets((prev) => [...prev, { key: "", value: "" }])}
            >
              <Plus className="h-3.5 w-3.5" />
              {t("common.add")}
            </Button>
          </div>
          <p className="text-xs text-muted">{t("settings.channelSecretsHint")}</p>
          {secrets.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted">
              {t("settings.channelNoSecrets")}
            </p>
          ) : (
            <div className="space-y-2">
              {secrets.map((row, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                  <Input
                    value={row.key}
                    disabled={row.mask !== undefined}
                    onChange={(e) =>
                      setSecrets((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, key: e.target.value } : r)),
                      )
                    }
                    placeholder="client_id"
                    className="font-mono text-xs"
                  />
                  <Input
                    value={row.value}
                    onChange={(e) =>
                      setSecrets((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)),
                      )
                    }
                    placeholder={row.mask ?? t("common.leaveEmptyKeep")}
                    className="font-mono text-xs"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t("common.remove")}
                    onClick={() => setSecrets((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? t("common.saving") : t("settings.channelSave")}
          </Button>
          {feedback ? (
            <p className={feedback.kind === "ok" ? "text-xs text-success" : "text-xs text-danger"}>
              {feedback.text}
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
